import React from "react";
import { NavItemType } from "./types";
import Link from "next/link";
import { ChevronDown } from "lucide-react";

interface NavItemProps extends NavItemType {
  onClick?: () => void;
  isActive?: boolean;
  hasDropdown?: boolean;
  isOpen?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({
  label,
  href,
  onClick,
  isActive = false,
  hasDropdown = false,
  isOpen = false,
}) => {
  const Component: React.ElementType = hasDropdown ? "button" : href ? Link : "button";
  const props = hasDropdown
    ? { type: "button" as const, onClick }
    : href
    ? { href: href as string }
    : { type: "button" as const, onClick };

  const isHighlighted = isActive || isOpen;

  const renderLabel = () => (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span>{label}</span>
      {hasDropdown && (
        <ChevronDown
          size={13}
          strokeWidth={2.4}
          className={`transition-transform duration-300 shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      )}
    </span>
  );

  return (
    <Component
      {...props}
      style={
        isHighlighted
          ? {
              background:
                "linear-gradient(252.81deg, #8ABA28 -24.42%, #056686 105.73%)",
            }
          : undefined
      }
      className={`relative overflow-hidden group flex items-center justify-center h-9 xl:h-10 px-3 xl:px-4 2xl:px-6 rounded-full text-[13px] xl:text-[14px] 2xl:text-[15px] font-normal tracking-tight transition-all duration-300 cursor-pointer ${
        isHighlighted ? "text-white shadow-xs" : "text-secondary-gray"
      }`}
    >
      <span
        className={`absolute inset-0 flex items-center justify-center h-full transition-transform duration-300 transform translate-y-0 whitespace-nowrap ${
          isHighlighted ? "" : "group-hover:translate-y-full"
        }`}
      >
        {renderLabel()}
      </span>
      {!isHighlighted && (
        <span className="absolute inset-0 flex items-center justify-center h-full transition-transform duration-300 transform -translate-y-full group-hover:translate-y-0 text-primary whitespace-nowrap">
          {renderLabel()}
        </span>
      )}

      <span className="invisible whitespace-nowrap">{renderLabel()}</span>
    </Component>
  );
};

export default NavItem;
